'use client'

import { memo } from 'react'
import Link from 'next/link'
import { Apple, ArrowRight, Globe, Monitor, Smartphone } from 'lucide-react'
import { NinxlScrollReveal, NinxlStaggerReveal, NinxlStaggerItem } from './ninxl-scroll-reveal'
import { NinxlSectionBackdrop, NinxlSectionHeader } from './ninxl-graphics'
import { NinxlNingenieProductHuntBadge } from './ninxl-product-hunt-badge'

type SurfaceCard = {
  href: string | null
  icon: typeof Monitor
  title: string
  desc: string
  badge: string
  primary?: boolean
  showProductHuntBadge?: boolean
}

const SURFACES: SurfaceCard[] = [
  {
    href: null,
    icon: Monitor,
    title: 'NINX Desktop',
    desc: 'Private agent on your Mac or PC. Local by default.',
    badge: 'You are here',
    primary: true,
  },
  {
    href: 'https://t.nin.in',
    icon: Globe,
    title: 'NINX Web',
    desc: 't.nin.in gives you full power in the browser when you need the cloud.',
    badge: 'Cloud when needed',
  },
  {
    href: 'https://apps.apple.com/app/ningenie-dont-ask-just-task/id6755959217',
    icon: Apple,
    title: 'Ningenie iOS',
    desc: 'Take NINX tasks with you. Android coming soon.',
    badge: 'On the go',
    showProductHuntBadge: true,
  },
]

/** Three-device sync flow with animated traveling dot */
const EcosystemFlow = () => (
  <div className="ninxl-ecosystem-flow relative mx-auto mb-10 max-w-2xl px-4">
    <div className="flex items-center justify-between gap-2 sm:gap-4">
      {[
        { icon: Monitor, label: 'Desktop' },
        { icon: Globe, label: 'Web' },
        { icon: Smartphone, label: 'Mobile' },
      ].map((node) => (
        <div key={node.label} className="flex flex-col items-center gap-2 z-[1]">
          <div className="rounded-2xl border border-[#21728d]/20 bg-white p-3.5 shadow-md shadow-[#21728d]/8">
            <node.icon className="h-5 w-5 text-[#21728d]" />
          </div>
          <span className="text-[10px] font-semibold uppercase tracking-wider text-[#88a1ac]">{node.label}</span>
        </div>
      ))}
    </div>
    {/* Connecting lines */}
    <div className="absolute left-[18%] right-[18%] top-[22px] flex items-center">
      <div className="ninxl-sync-line h-px flex-1 bg-gradient-to-r from-[#21728d]/10 via-[#21728d]/50 to-[#21728d]/10" />
      <div className="ninxl-ecosystem-dot absolute left-[18%] h-2 w-2 rounded-full bg-[#21728d] shadow-[0_0_8px_rgba(33,114,141,0.5)]" />
      <div className="ninxl-sync-line h-px flex-1 bg-gradient-to-r from-[#21728d]/10 via-[#21728d]/50 to-[#21728d]/10" />
    </div>
    <p className="mt-4 text-center text-[11px] text-[#88a1ac]">
      One account · same sessions · pick up where you left off
    </p>
  </div>
)

export const ContinueAnywhereSection = memo(function ContinueAnywhereSection() {
  return (
    <section className="py-20 border-y border-slate-200 bg-gradient-to-b from-white via-[#f6f9fd]/40 to-white relative overflow-hidden">
      <NinxlSectionBackdrop variant="everywhere" />
      <div className="absolute inset-0 -z-10 bg-[radial-gradient(ellipse_70%_50%_at_50%_50%,rgba(33,114,141,0.05),transparent)] pointer-events-none" />

      <div className="mx-auto max-w-5xl px-6 md:px-8">
        <NinxlScrollReveal className="mb-8">
          <NinxlSectionHeader
            label="One account. Everywhere."
            title="Start on desktop. Finish anywhere."
            description="Begin a task on NINX, check in on your phone, or continue in the browser. Everything stays in sync."
            className="mb-0 [&_h2]:text-3xl [&_p]:text-sm"
          />
        </NinxlScrollReveal>

        <NinxlScrollReveal delay={0.08}>
          <EcosystemFlow />
        </NinxlScrollReveal>

        <NinxlStaggerReveal className="grid gap-4 md:grid-cols-3 max-w-4xl mx-auto" stagger={0.1}>
          {SURFACES.map((surface) => {
            const inner = (
              <div
                className={`ninxl-surface-card group relative flex h-full flex-col rounded-3xl border p-5 transition duration-300 hover:-translate-y-1 hover:shadow-xl ${
                  surface.primary
                    ? 'border-[#21728d]/30 bg-white shadow-lg shadow-[#21728d]/10 ring-1 ring-[#21728d]/10'
                    : 'border-slate-200 bg-white hover:border-[#21728d]/25 hover:shadow-[#21728d]/8'
                }`}
              >
                <span
                  className={`mb-3 inline-flex w-fit rounded-full px-2.5 py-0.5 text-[9px] font-bold uppercase tracking-wider ${
                    surface.primary ? 'bg-[#21728d] text-white' : 'bg-slate-100 text-slate-600'
                  }`}
                >
                  {surface.badge}
                </span>
                <div className="mb-4 inline-flex rounded-2xl border border-slate-100 bg-[#F8FAFC] p-3 transition duration-300 group-hover:scale-105 group-hover:border-[#21728d]/20">
                  <surface.icon className="h-6 w-6 text-[#21728d]" />
                </div>
                <div className="font-semibold tracking-tight mb-1 text-slate-900">{surface.title}</div>
                <p className="text-sm text-slate-600 flex-1">{surface.desc}</p>
                {surface.showProductHuntBadge ? (
                  <div className="mt-4">
                    <NinxlNingenieProductHuntBadge size="compact" />
                  </div>
                ) : null}
                {surface.href ? (
                  surface.showProductHuntBadge ? (
                    <Link
                      href={surface.href}
                      target="_blank"
                      className="mt-4 inline-flex items-center gap-1 text-xs font-semibold text-[#21728d]"
                    >
                      Open <ArrowRight className="h-3 w-3" />
                    </Link>
                  ) : (
                    <span className="mt-4 inline-flex items-center gap-1 text-xs font-semibold text-[#21728d]">
                      Open <ArrowRight className="h-3 w-3" />
                    </span>
                  )
                ) : null}
              </div>
            )

            return (
              <NinxlStaggerItem key={surface.title}>
                {surface.href && !surface.showProductHuntBadge ? (
                  <Link href={surface.href} target="_blank" className="block h-full">
                    {inner}
                  </Link>
                ) : (
                  inner
                )}
              </NinxlStaggerItem>
            )
          })}
        </NinxlStaggerReveal>
      </div>
    </section>
  )
})